import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import Layout from "../components/Layout";
import { useAuth } from "../contexts/AuthContext";
import { TrendingUp, Eye, MousePointerClick, Users, Link2, FolderOpen, Image as ImageIcon, Tag, Megaphone, CheckSquare, Square, BarChart3, Filter } from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";

const PERIODS = [
  { v: "7d",  label: "7 dager" },
  { v: "30d", label: "30 dager" },
  { v: "90d", label: "90 dager" },
  { v: "all", label: "Alle" },
];

const TYPE_LABELS = {
  link:  { label: "Lenker",      icon: Link2,     color: "#D11A5B" },
  album: { label: "Album",       icon: FolderOpen, color: "#D4A574" },
  media: { label: "Bilder/Film", icon: ImageIcon, color: "#7FA88E" },
  promo: { label: "Promoer",     icon: Tag,       color: "#5FA59E" },
  ad:    { label: "Annonser",    icon: Megaphone, color: "#B26A3F" },
};

// Pastell-fargesett for sammenligningslinjer (kontrasterer mot magenta-aksenten)
const COMPARE_COLORS = ["#D11A5B", "#D4A574", "#7FA88E", "#5FA59E", "#B26A3F", "#5BA5C9", "#9B7BB8", "#C7CBD1"];
const CONTEXT_COLORS = ["#EAEAEA", "#A8B0BD", "#D4A574", "#5BA5C9", "#9B7BB8", "#C8A2C8", "#7FA88E"];

// Definer kontekst-linjer
const CONTEXT_OPTS = [
  { id: "views",         label: "Sidevisninger",      icon: Eye },
  { id: "clicks",        label: "Klikk totalt",       icon: MousePointerClick },
  { id: "new_followers", label: "Nye følgere",        icon: Users },
  { id: "total_link",    label: "Lenker (totalt)",    icon: Link2 },
  { id: "total_album",   label: "Album (totalt)",     icon: FolderOpen },
  { id: "total_media",   label: "Bilder/Film (totalt)", icon: ImageIcon },
  { id: "total_promo",   label: "Promoer (totalt)",   icon: Tag },
  { id: "total_ad",      label: "Annonser (totalt)",  icon: Megaphone },
];

const CONTEXT_LABEL = Object.fromEntries(CONTEXT_OPTS.map(o => [o.id, o.label]));

const formatDay = (iso) => {
  // "2026-05-21" → "21.05"
  if (!iso || iso.length < 10) return iso;
  return `${iso.slice(8, 10)}.${iso.slice(5, 7)}`;
};

const pct = (num, denom, digits = 1) => {
  if (!denom || denom === 0) return null;
  return ((num / denom) * 100).toFixed(digits);
};

export default function MinInnsikt() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [period, setPeriod] = useState("30d");
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState([]);  // [{target_type, target_id, label}]
  const [selectedContext, setSelectedContext] = useState([]);  // ["views","clicks",...]
  const [followerFilter, setFollowerFilter] = useState(null);  // null | 7 | 30 | 90
  const [compareData, setCompareData] = useState(null);
  const [comparing, setComparing] = useState(false);

  // Auth gate
  useEffect(() => {
    if (!loading) {
      if (!user) { nav("/?tab=login"); return; }
      if (!user.is_creator) { nav("/bli-skaper"); return; }
    }
  }, [user, loading, nav]);

  // Hent stats
  useEffect(() => {
    if (!user?.is_creator) return;
    setBusy(true);
    api.get(`/me/creator-stats?period=${period}`)
      .then((r) => setStats(r.data))
      .catch(() => setStats(null))
      .finally(() => setBusy(false));
    // Reset sammenligning når periode endres
    setSelected([]);
    setSelectedContext([]);
    setFollowerFilter(null);
    setCompareData(null);
  }, [period, user?.is_creator]);

  const totals = stats?.totals || { views: 0, clicks: 0, new_followers: 0 };
  const byType = stats?.by_type || {};

  const isSelected = (t, id) => selected.some((s) => s.target_type === t && s.target_id === id);

  const toggleSelect = (t, id, label) => {
    if (isSelected(t, id)) {
      setSelected(selected.filter((s) => !(s.target_type === t && s.target_id === id)));
    } else {
      if (selected.length + selectedContext.length >= 12) return;
      setSelected([...selected, { target_type: t, target_id: id, label }]);
    }
    setCompareData(null);
  };

  const toggleContext = (id) => {
    if (selectedContext.includes(id)) {
      setSelectedContext(selectedContext.filter(c => c !== id));
    } else {
      if (selectedContext.length + selected.length >= 12) return;
      setSelectedContext([...selectedContext, id]);
    }
    setCompareData(null);
  };

  const runCompare = async () => {
    if (!selected.length && !selectedContext.length) return;
    const p = period === "all" ? "90d" : period;
    setComparing(true);
    try {
      const r = await api.post("/me/creator-stats/compare", {
        items: selected.map((s) => ({ target_type: s.target_type, target_id: s.target_id })),
        context: selectedContext,
        period: p,
        only_new_followers_within_days: followerFilter,
      });
      setCompareData(r.data);
    } finally {
      setComparing(false);
    }
  };

  // Merge data for chart – elementer på venstre akse, kontekst på høyre akse
  const { chartData, elementSeries, contextSeries } = useMemo(() => {
    const empty = { chartData: [], elementSeries: [], contextSeries: [] };
    if (!compareData?.series?.length) return empty;
    const days = compareData.series[0].data.map(d => d.day);
    const elements = compareData.series.filter(s => s.kind === "element");
    const ctx = compareData.series.filter(s => s.kind === "context");
    const rows = days.map((day, i) => {
      const row = { day };
      elements.forEach((s, idx) => { row[`e${idx}`] = s.data[i]?.value ?? 0; });
      ctx.forEach((s, idx) => { row[`c${idx}`] = s.data[i]?.value ?? 0; });
      return row;
    });
    return { chartData: rows, elementSeries: elements, contextSeries: ctx };
  }, [compareData]);

  return (
    <Layout>
      <div className="mb-6">
        <Link to={`/skaper/${user?.username || ""}`} className="font-meta text-[var(--cream-soft)] hover:text-[var(--cream)] text-xs">
          ← TILBAKE TIL SKAPERSIDEN
        </Link>
        <h1 className="font-fraunces italic text-4xl text-[var(--cream)] mt-3 flex items-center gap-3">
          <TrendingUp className="h-7 w-7 text-[var(--magenta)]" /> Min innsikt
        </h1>
        <p className="text-[var(--cream-soft)] text-sm mt-2 font-manrope max-w-2xl">
          Statistikk over hva besøkende klikker på på skapersiden din. Velg elementer du vil sammenligne nedenfor og bygg en graf på tvers.
        </p>
      </div>

      {/* PERIODE-FILTER */}
      <div className="flex flex-wrap gap-2 mb-8" data-testid="period-filter">
        {PERIODS.map((p) => (
          <button
            key={p.v}
            onClick={() => setPeriod(p.v)}
            data-testid={`period-${p.v}`}
            className={`px-4 py-2 text-xs font-meta tracking-[0.18em] border transition ${
              period === p.v
                ? "border-[var(--magenta)] text-[var(--magenta)] bg-[var(--magenta)]/10"
                : "border-white/15 text-[var(--cream-soft)] hover:border-[var(--magenta)]/60 hover:text-[var(--cream)]"
            }`}
          >
            {p.label.toUpperCase()}
          </button>
        ))}
      </div>

      {busy && <div className="font-meta text-[var(--cream-soft)] py-10">Laster…</div>}

      {!busy && stats && (
        <>
          {/* TOTALS */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8" data-testid="totals-grid">
            <StatCard
              icon={Eye} label="Sidevisninger" value={totals.views}
              accent="#D11A5B"
            />
            <StatCard
              icon={MousePointerClick} label="Klikk totalt" value={totals.clicks}
              accent="#D4A574"
              subtitle={pct(totals.clicks, totals.views) !== null
                ? `${pct(totals.clicks, totals.views)}% klikk-rate (av visninger)`
                : null}
            />
            <StatCard
              icon={Users} label="Nye følgere" value={totals.new_followers}
              accent="#7FA88E"
              subtitle={pct(totals.new_followers, totals.views) !== null
                ? `${pct(totals.new_followers, totals.views)}% konvertering (av visninger)`
                : null}
            />
          </div>

          {/* BY TYPE BREAKDOWN */}
          <div className="mb-8">
            <h2 className="font-fraunces italic text-2xl text-[var(--cream)] mb-4">Klikk fordelt per type</h2>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="bytype-grid">
              {Object.entries(TYPE_LABELS).map(([key, cfg]) => {
                const Icon = cfg.icon;
                const count = byType[key] || 0;
                const share = pct(count, totals.clicks);
                return (
                  <div
                    key={key}
                    className="border border-white/10 p-4 bg-[var(--noir-elev)] flex flex-col gap-2"
                    style={{ borderLeft: `3px solid ${cfg.color}` }}
                    data-testid={`bytype-${key}`}
                  >
                    <div className="flex items-center gap-2 font-meta text-[var(--cream-soft)] text-[10px]">
                      <Icon className="h-3.5 w-3.5" style={{ color: cfg.color }} />
                      {cfg.label.toUpperCase()}
                    </div>
                    <div className="font-fraunces italic text-3xl text-[var(--cream)] tabular-nums leading-none">
                      {count}
                    </div>
                    {share !== null && totals.clicks > 0 && (
                      <div className="font-meta text-[10px]" style={{ color: cfg.color, opacity: 0.85 }}>
                        {share}% av alle klikk
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* SAMMENLIGNING */}
          <div className="mb-8 border border-[var(--magenta)]/20 p-5 bg-[var(--noir-elev)]" data-testid="compare-section">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <h2 className="font-fraunces italic text-2xl text-[var(--cream)] flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-[var(--magenta)]" /> Sammenlign elementer
              </h2>
              <div className="flex gap-2 items-center">
                <span className="text-xs font-meta text-[var(--cream-faint)]">
                  {selected.length + selectedContext.length}/12 valgt
                </span>
                {(selected.length + selectedContext.length) > 0 && (
                  <>
                    <button
                      onClick={() => { setSelected([]); setSelectedContext([]); setFollowerFilter(null); setCompareData(null); }}
                      className="text-xs font-meta text-[var(--cream-soft)] hover:text-[var(--cream)] underline-offset-4 hover:underline"
                      data-testid="compare-clear"
                    >
                      Nullstill
                    </button>
                    <button
                      onClick={runCompare}
                      disabled={comparing}
                      className="px-4 py-1.5 text-xs font-meta tracking-[0.18em] bg-[var(--magenta)] text-black disabled:opacity-50"
                      data-testid="compare-run"
                    >
                      {comparing ? "BYGGER…" : "BYGG GRAF"}
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* KONTEKST-LINJER */}
            <div className="mb-4" data-testid="context-options">
              <div className="font-meta text-[10px] uppercase tracking-[0.2em] text-[var(--cream-faint)] mb-2">
                Kontekst-linjer (totaler – stiplete linjer, høyre Y-akse)
              </div>
              <div className="flex flex-wrap gap-1.5">
                {CONTEXT_OPTS.map((opt) => {
                  const sel = selectedContext.includes(opt.id);
                  const Icon = opt.icon;
                  return (
                    <button
                      key={opt.id}
                      onClick={() => toggleContext(opt.id)}
                      className={`flex items-center gap-1.5 px-2.5 py-1 text-xs font-meta border transition ${
                        sel
                          ? "border-[var(--magenta)] text-[var(--cream)] bg-[var(--magenta)]/15"
                          : "border-white/15 text-[var(--cream-soft)] hover:border-[var(--magenta)]/60"
                      }`}
                      data-testid={`ctx-${opt.id}`}
                    >
                      <Icon className="h-3 w-3" />
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* FØLGER-FILTER */}
            <div className="mb-4" data-testid="follower-filter">
              <div className="font-meta text-[10px] uppercase tracking-[0.2em] text-[var(--cream-faint)] mb-2 flex items-center gap-1.5">
                <Filter className="h-3 w-3" /> Filter: vis kun klikk fra besøkende som ble nye følgere
              </div>
              <div className="flex gap-1.5">
                {[
                  { v: null, label: "Av (alle klikk)" },
                  { v: 7, label: "Siste 7 dager" },
                  { v: 30, label: "Siste 30 dager" },
                  { v: 90, label: "Siste 90 dager" },
                ].map((opt) => (
                  <button
                    key={String(opt.v)}
                    onClick={() => { setFollowerFilter(opt.v); setCompareData(null); }}
                    className={`px-2.5 py-1 text-xs font-meta border transition ${
                      followerFilter === opt.v
                        ? "border-[var(--magenta)] text-[var(--magenta)] bg-[var(--magenta)]/10"
                        : "border-white/15 text-[var(--cream-soft)] hover:border-[var(--magenta)]/60"
                    }`}
                    data-testid={`follower-${opt.v ?? 'off'}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              {followerFilter && (
                <div className="font-meta text-[10px] text-[var(--magenta)] mt-2 opacity-80">
                  Aktiv: viser kun klikk fra besøkende som ble følger innen siste {followerFilter} dager.
                </div>
              )}
            </div>

            {(selected.length + selectedContext.length) === 0 && (
              <div className="text-sm text-[var(--cream-faint)] font-manrope">
                Hak av elementer i tabellene under for å sammenligne klikk over tid.
                <span className="block mt-1 text-[11px]">
                  Tips: velg flere fra <strong>samme kategori</strong> (eks. 4 lenker) for å sammenligne hva som presterer best.
                </span>
              </div>
            )}

            {compareData && chartData.length > 0 && (
              <div className="mt-4" data-testid="compare-chart">
                <ResponsiveContainer width="100%" height={360}>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                    <XAxis dataKey="day" stroke="#5E574E" tick={{ fontSize: 10 }} tickFormatter={formatDay} />
                    <YAxis yAxisId="left" stroke="#D11A5B" tick={{ fontSize: 10 }} allowDecimals={false}
                           label={{ value: "Elementer (klikk)", angle: -90, position: "insideLeft", fill: "#D11A5B", fontSize: 10 }}
                    />
                    <YAxis yAxisId="right" orientation="right" stroke="#A8B0BD" tick={{ fontSize: 10 }} allowDecimals={false}
                           label={{ value: "Kontekst (totaler)", angle: 90, position: "insideRight", fill: "#A8B0BD", fontSize: 10 }}
                    />
                    <Tooltip
                      contentStyle={{ background: "#0A0A0A", border: "1px solid rgba(255,255,255,0.15)", fontSize: 12 }}
                      labelStyle={{ color: "#F2EBE0" }}
                      labelFormatter={formatDay}
                    />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    {elementSeries.map((s, idx) => (
                      <Line
                        key={`e${idx}`}
                        yAxisId="left"
                        type="monotone"
                        dataKey={`e${idx}`}
                        name={selected[idx]?.label || `Element ${idx + 1}`}
                        stroke={COMPARE_COLORS[idx % COMPARE_COLORS.length]}
                        strokeWidth={2}
                        dot={{ r: 2 }}
                        activeDot={{ r: 5 }}
                      />
                    ))}
                    {contextSeries.map((s, idx) => (
                      <Line
                        key={`c${idx}`}
                        yAxisId="right"
                        type="monotone"
                        dataKey={`c${idx}`}
                        name={CONTEXT_LABEL[s.ctx_id] || s.ctx_id}
                        stroke={CONTEXT_COLORS[idx % CONTEXT_COLORS.length]}
                        strokeWidth={1.5}
                        strokeDasharray="6 3"
                        dot={false}
                        activeDot={{ r: 4 }}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* PER-ELEMENT TABELLER */}
          {Object.entries(TYPE_LABELS).map(([key, cfg]) => {
            const rows = stats.elements?.[key] || [];
            const categoryTotal = byType[key] || 0;
            return (
              <ElementTable
                key={key}
                typeKey={key}
                cfg={cfg}
                rows={rows}
                categoryTotal={categoryTotal}
                selected={selected}
                isSelected={isSelected}
                toggleSelect={toggleSelect}
              />
            );
          })}
        </>
      )}
    </Layout>
  );
}

function StatCard({ icon: Icon, label, value, accent, subtitle }) {
  return (
    <div
      className="border border-white/10 p-5 bg-[var(--noir-elev)]"
      style={{ borderLeft: `3px solid ${accent}` }}
    >
      <div className="flex items-center gap-2 font-meta text-[var(--cream-soft)] text-[10px] mb-3">
        <Icon className="h-3.5 w-3.5" style={{ color: accent }} />
        {label.toUpperCase()}
      </div>
      <div className="font-fraunces italic text-4xl text-[var(--cream)] tabular-nums leading-none">
        {value}
      </div>
      {subtitle && (
        <div className="font-meta text-[10px] text-[var(--cream-faint)] mt-2.5" style={{ color: accent, opacity: 0.85 }}>
          {subtitle}
        </div>
      )}
    </div>
  );
}

function ElementTable({ typeKey, cfg, rows, categoryTotal, selected, isSelected, toggleSelect }) {
  const Icon = cfg.icon;
  const selectedInCategory = selected.filter((s) => s.target_type === typeKey).length;
  return (
    <div className="mb-8" data-testid={`table-${typeKey}`}>
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <Icon className="h-4 w-4" style={{ color: cfg.color }} />
        <h3 className="font-fraunces italic text-xl text-[var(--cream)]">{cfg.label}</h3>
        <span className="font-meta text-[10px] text-[var(--cream-faint)] ml-2">
          {rows.length} {rows.length === 1 ? "element" : "elementer"}
        </span>
        {selectedInCategory > 0 && (
          <span
            className="font-meta text-[10px] px-2 py-0.5 ml-auto"
            style={{ background: `${cfg.color}22`, color: cfg.color }}
          >
            {selectedInCategory} valgt i denne kategorien
          </span>
        )}
      </div>
      {rows.length === 0 ? (
        <div className="text-sm text-[var(--cream-faint)] font-manrope border border-dashed border-white/10 p-4 text-center">
          Ingen klikk på {cfg.label.toLowerCase()} i valgt periode enda.
        </div>
      ) : (
        <div className="border border-white/10 divide-y divide-white/5">
          {rows.map((row) => {
            const sel = isSelected(typeKey, row.id);
            const share = pct(row.clicks, categoryTotal);
            return (
              <button
                key={row.id}
                onClick={() => toggleSelect(typeKey, row.id, row.label)}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition ${
                  sel ? "bg-[var(--magenta)]/10" : "hover:bg-white/5"
                }`}
                data-testid={`row-${typeKey}-${row.id}`}
              >
                {sel ? (
                  <CheckSquare className="h-4 w-4 text-[var(--magenta)] shrink-0" />
                ) : (
                  <Square className="h-4 w-4 text-[var(--cream-faint)] shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-[var(--cream)] truncate">{row.label}</div>
                  {row.platform && row.platform !== row.label && (
                    <div className="font-meta text-[10px] text-[var(--cream-faint)] mt-0.5">
                      {row.platform.toUpperCase()}
                    </div>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <div className="font-fraunces italic text-2xl text-[var(--cream)] tabular-nums leading-none">
                    {row.clicks}
                  </div>
                  {share !== null && categoryTotal > 0 && (
                    <div className="font-meta text-[10px] mt-1" style={{ color: cfg.color, opacity: 0.85 }}>
                      {share}% av {cfg.label.toLowerCase()}
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
